/**
 * How big is this deck, how high, and what goes on it?
 *
 * Asked before a deck estimate is generated. A deck is not measured off a
 * report: the rep types its size, and every post, joist, board and baluster
 * follows from it by rule. Jonathon, 2026-10-11, asked whether square footage
 * was enough; it prices the labour, but two decks of the same size can need
 * very different lumber, so the form asks for the width and depth and shows
 * the square feet. See packages/core/src/deck.ts.
 *
 * The same shape as the flat roof, metal panel and siding forms.
 */

import {
  deckSquareFeet,
  deckWarnings,
  defaultRailingLf,
  railingLfOf,
  type DeckAnswers,
} from "@roof-hawk/core";

import { t } from "../../i18n";

export function DeckQuestions({
  answers,
  onChange,
}: {
  answers: DeckAnswers;
  onChange: (answers: DeckAnswers) => void;
}) {
  const set = <K extends keyof DeckAnswers>(key: K, value: DeckAnswers[K]) =>
    onChange({ ...answers, [key]: value });

  const feet = (
    label: string,
    key: "widthFt" | "depthFt" | "heightFt" | "stairWidthFt",
    hint?: string,
  ) => (
    <label className="flatq-row">
      <span>{t(label)}</span>
      <input
        type="number"
        min={0}
        step={0.5}
        aria-label={t(label)}
        value={answers[key] || ""}
        onChange={(e) => set(key, Math.max(0, Number(e.target.value) || 0))}
      />
      {hint && <span className="flatq-hint">{t(hint)}</span>}
    </label>
  );

  const area = deckSquareFeet(answers);
  const warnings = deckWarnings(answers);

  return (
    <div className="flatq">
      <h3>{t("How big is the deck?")}</h3>
      {feet("Width along the house (ft)", "widthFt")}
      {feet("Depth out from the house (ft)", "depthFt")}
      <p className="flatq-hint">
        {area > 0
          ? t(
              "{area} square feet. The crew is paid on this, and the customer reads it.",
              { area },
            )
          : t("The square feet are worked out from these two.")}
      </p>
      {feet(
        "Height off the ground (ft)",
        "heightFt",
        "Sets how long the posts are and how many steps down.",
      )}

      <label className="flatq-check">
        <input
          type="checkbox"
          checked={answers.secondStory}
          onChange={(e) => set("secondStory", e.target.checked)}
        />
        {t("Second-story deck")}
      </label>
      <label className="flatq-check">
        <input
          type="checkbox"
          checked={answers.replacing}
          onChange={(e) => set("replacing", e.target.checked)}
        />
        {t("Replacing a deck")}
      </label>
      <p className="flatq-hint">
        {answers.secondStory
          ? t("The crew is paid the second-story rate.")
          : t("The crew is paid the one-story rate.")}{" "}
        {answers.replacing
          ? t("The scope says the old deck is torn down and hauled away first.")
          : t("A new deck: the scope says nothing is torn down.")}
      </p>

      <label className="flatq-check">
        <input
          type="checkbox"
          checked={answers.attached}
          onChange={(e) => set("attached", e.target.checked)}
        />
        {t("Fastened to the house")}
      </label>
      <p className="flatq-hint">
        {answers.attached
          ? t(
              "A ledger board on the house, flashed, with tension ties; one row of posts at the front.",
            )
          : t(
              "Freestanding: a row of posts at the front and the back, and no ledger.",
            )}
      </p>

      <h3>{t("Steps and railing")}</h3>
      <label className="flatq-row">
        <span>{t("Sets of steps")}</span>
        <input
          type="number"
          min={0}
          step={1}
          aria-label={t("Sets of steps")}
          value={answers.stairSets}
          onChange={(e) =>
            set(
              "stairSets",
              Math.max(0, Math.round(Number(e.target.value) || 0)),
            )
          }
        />
        <span className="flatq-hint">
          {t(
            "The first set is in the crew’s rate; each one after it is charged on its own.",
          )}
        </span>
      </label>
      {answers.stairSets > 0 && feet("Width of each set (ft)", "stairWidthFt")}
      <label className="flatq-check">
        <input
          type="checkbox"
          checked={answers.railingRequired}
          onChange={(e) => set("railingRequired", e.target.checked)}
        />
        {t("Railing required")}
      </label>
      {answers.railingRequired ? (
        <label className="flatq-row">
          <span>{t("Railing (ft)")}</span>
          <input
            type="number"
            min={0}
            step={1}
            aria-label={t("Railing (ft)")}
            value={railingLfOf(answers) || ""}
            onChange={(e) =>
              set("railingLf", Math.max(0, Number(e.target.value) || 0))
            }
          />
          <span className="flatq-hint">
            {answers.railingLf === null
              ? t(
                  "Every open side, less the stair openings. Change it if a side has none.",
                )
              : t("The open sides come to {ft} ft.", {
                  ft: defaultRailingLf(answers),
                })}{" "}
            {t("The steps get handrails on both sides on top of this.")}
          </span>
        </label>
      ) : (
        <p className="flatq-hint">
          {t("No railing round the deck, and no handrails on the steps.")}
        </p>
      )}

      {warnings.map((warning) => (
        <div
          className="config-warn"
          role="alert"
          key={warning}
          style={{ marginTop: 8 }}
        >
          {t(warning)}
        </div>
      ))}
    </div>
  );
}
